// MA-625 — staged add/sub renderers (concrete -> bridging -> abstract).
//
// See src/lib/engine/mathVisualStage.ts for the stage decision (band ->
// stage) and the projection that swaps a `groups`/`ten_frame`/`take_away`
// mathVisual's `kind` to one of these three at serve time, WITHOUT touching
// the mapper's own output (contentToEngineItem.ts stays byte-identical —
// staging is a session-screen-time projection over the mapper's payload).
//
// PEDAGOGY (the MA-625 finding): two separated groups counted-all is the
// EARLIEST addition stage — right for a child still counting all, filler
// for one who counts on from a known number. A crossed-out-dots picture is
// a take-away model where the uncrossed count IS the answer. Neither is
// wrong; both are the wrong MODEL for a child past that stage. These three
// renderers draw the later stages:
//
//   count_on   — `a` as a numeral card, "+", then `b` dots in the b colour.
//                Nothing on screen sums to the answer; the child counts ON
//                from the card.
//   count_back — `a` as a numeral card, "-", then `b` crossed-out dots.
//                The child counts BACK from the known number; no intact
//                group of dots ever equals the remaining amount.
//   equation   — two numeral cards and the operator glyph. No dots at all —
//                pure recall/strategy stage.
//
// ANSWER-SAFETY BY CONSTRUCTION: every renderer below reads only `a`, `b`,
// and `op` from its props. None of the three ever destructures or reads the
// pass-through answer field carried alongside them on the mathVisual payload
// (kept there only for the a+b/a-b reconstruction gate upstream) — so there
// is no code path in this file that could print it. Pinned by a source grep
// in mathVisualStage.test.mjs (AUDIT_STEPS) and independently swept by that
// same test's registry-level render-to-markup leak check over every real
// eligible item, at every stage.
import type { MathRendererProps } from "./rendererRegistry";

const CARD_FILL = "var(--pal-cream, #faf3e8)";
const CARD_STROKE = "var(--color-border-secondary, #cdbfae)";
const CARD_TEXT = "var(--color-text-primary, #3d3228)";
const DOT_B = "var(--pal-green, #5a9e6f)";
const DOT_REMOVED_BG = "var(--color-bg-secondary, #f5efe3)";
const DOT_REMOVED_STROKE = "var(--color-border-secondary, #cdbfae)";
const X_COLOR = "var(--color-error, #c0392b)";
const OP_COLOR = "var(--color-text-secondary, #7a6f60)";

const CARD = 76; // numeral-card square edge
const R = 13; // dot radius (matches Dots()/TakeAwayRenderer)
const CELL = 34; // dot pitch
const PAD = 18; // first dot center offset
const COLS = 5; // dots per row
// MA-707 — EXPORTED so mathVisualStage.ts's DOT_STAGE_MAX_B rule reads the
// same number: a count_on/count_back payload with b above this would be
// silently truncated here (a false count), so the stage mapper never emits
// one (it projects to `equation` instead).
export const MAX_DOTS = 20; // safety clamp, mirrors TakeAwayRenderer's MAX_A

function safeInt(n: unknown, max = Infinity): number {
  const v = Math.floor(Number.isFinite(n as number) ? (n as number) : 0);
  return Math.max(0, Math.min(max, v));
}

function safeCardValue(n: unknown): string {
  const v = Math.floor(Number(n));
  return Number.isFinite(v) && v >= 0 ? String(v) : "?";
}

function opGlyph(op: "+" | "-" | undefined): string {
  return op === "-" ? "−" : "+";
}

/** A bordered numeral card, drawn at (x,0). Mirrors NumeralCardRenderer's
 *  proportions, scaled to `size` (defaults to the full CARD=76 used by the
 *  three staged renderers below) so it sits comfortably beside dots or a
 *  second card. MA-647 reuses this at a smaller size (CAPTION_CARD=52) for
 *  the concrete-stage operand caption in TakeAwayRenderer/Groups — exported
 *  so those renderers never duplicate the card styling. */
export function numeralCard(x: number, n: unknown, keyPrefix: string, size: number = CARD) {
  const label = safeCardValue(n);
  const inner = size - 4;
  const fontSize = Math.round(size * 0.55);
  const textY = Math.round(size / 2 + size * 0.2);
  const rx = Math.round(size * 0.16);
  return (
    <g key={keyPrefix} transform={`translate(${x}, 0)`}>
      <rect x="2" y="2" width={inner} height={inner} rx={rx}
        fill={CARD_FILL} stroke={CARD_STROKE} strokeWidth="2.5" />
      <text x={size / 2} y={textY} textAnchor="middle" fontSize={fontSize} fontWeight="700"
        fontFamily="inherit" fill={CARD_TEXT}>{label}</text>
    </g>
  );
}

// MA-647 — concrete take-away/groups OPERAND CAPTION. `TakeAwayRenderer`
// (always two operands) and `Groups`' two-group (`hasTwo`) arm draw this
// ABOVE the dots: two numeral cards + the operator glyph, smaller than the
// staged EquationRenderer's cards (CAPTION_CARD=52 vs CARD=76) so the whole
// picture still fits maxWidth 360 and adds only ~66px of height (cardSize +
// a 14px gap before the dots) — well under the spec's ~70px budget. There is
// NO "=" and NO answer slot: exactly the EquationRenderer contract, reused
// here rather than duplicated. ANSWER-SAFETY: `OperandCaption` reads only
// `a`, `b`, `op` — never `total` — so it can never print the answer, by the
// same construction as CountOnRenderer/CountBackRenderer/EquationRenderer
// above (see mathVisualStage.test.mjs's source pin, which greps this file
// for the three staged renderers; TakeAwayRenderer/Groups are pinned
// separately by the caller's own tests since they live in other files).
export const CAPTION_CARD = 52;
const CAPTION_OP_W = 30;

/** The (width, height) an `OperandCaption` occupies at a given card size —
 *  callers use this to lay out the caption above their own dots without
 *  guessing at numeralCard's internal proportions. */
export function operandCaptionSize(cardSize: number = CAPTION_CARD, opW: number = CAPTION_OP_W) {
  return { width: cardSize * 2 + opW, height: cardSize };
}

export function OperandCaption({
  a, b, op, x = 0, y = 0, cardSize = CAPTION_CARD, opW = CAPTION_OP_W,
}: {
  a: unknown;
  b: unknown;
  op?: "+" | "-";
  x?: number;
  y?: number;
  cardSize?: number;
  opW?: number;
}) {
  return (
    // MA-CLOSURE3 (MA-647 caption probe) — data-testid marks the caption's
    // ACTUAL rendered presence, so a test can verify operandCaptionShownForKind
    // (rendererRegistry.tsx) against the real markup rather than trusting the
    // pure function's own claim. Cosmetic-inert (SVG ignores unknown data-*).
    <g transform={`translate(${x}, ${y})`} data-testid="operand-caption">
      {numeralCard(0, a, "cap-a", cardSize)}
      <text x={cardSize + opW / 2} y={Math.round(cardSize / 2 + cardSize * 0.2)} textAnchor="middle"
        fontSize={Math.round(cardSize * 0.4)} fill={OP_COLOR}>{opGlyph(op)}</text>
      {numeralCard(cardSize + opW, b, "cap-b", cardSize)}
    </g>
  );
}

function dotsCols(n: number): number {
  return Math.min(COLS, Math.max(1, n || 1));
}
function dotsRows(n: number): number {
  return Math.max(1, Math.ceil(Math.max(1, n) / COLS));
}
function dotsWidth(n: number): number {
  return PAD * 2 + (dotsCols(n) - 1) * CELL;
}
function dotsHeight(n: number): number {
  return PAD * 2 + (dotsRows(n) - 1) * CELL;
}

/** MA-625 CountOnRenderer — `a` as a numeral card, "+", then `b` intact
 *  dots in the b colour. Never draws `a` as dots (the whole point of
 *  bridging is that the known addend is no longer a countable picture). */
export function CountOnRenderer({ a, b, op }: MathRendererProps) {
  const dots = safeInt(b, MAX_DOTS);
  const dW = dotsWidth(dots || 1);
  const dH = dotsHeight(dots || 1);
  const opX = CARD + 14;
  const dotsX = opX + 34;
  const width = dotsX + dW;
  const height = Math.max(CARD, dH);
  const dotsYOffset = (height - dH) / 2;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ maxWidth: 360 }} role="img"
      aria-label={`Numeral card ${safeCardValue(a)}, and ${dots} more.`}>
      <g transform={`translate(0, ${(height - CARD) / 2})`}>{numeralCard(0, a, "card-a")}</g>
      <text x={opX + 12} y={height / 2 + 10} textAnchor="middle" fontSize="30" fill={OP_COLOR}>{opGlyph(op)}</text>
      <g transform={`translate(${dotsX}, ${dotsYOffset})`}>
        {Array.from({ length: dots }).map((_, i) => (
          <circle key={i} cx={PAD + (i % COLS) * CELL} cy={PAD + Math.floor(i / COLS) * CELL} r={R} fill={DOT_B} />
        ))}
      </g>
    </svg>
  );
}

/** MA-625 CountBackRenderer — `a` as a numeral card, "-", then `b` CROSSED
 *  dots. There is no intact group anywhere on screen whose count equals the
 *  remaining amount — the child counts BACK from the known number. */
export function CountBackRenderer({ a, b, op }: MathRendererProps) {
  const dots = safeInt(b, MAX_DOTS);
  const dW = dotsWidth(dots || 1);
  const dH = dotsHeight(dots || 1);
  const opX = CARD + 14;
  const dotsX = opX + 34;
  const width = dotsX + dW;
  const height = Math.max(CARD, dH);
  const dotsYOffset = (height - dH) / 2;
  const xArm = (R * 0.6).toFixed(1);
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ maxWidth: 360 }} role="img"
      aria-label={`Numeral card ${safeCardValue(a)}, take away ${dots}.`}>
      <g transform={`translate(0, ${(height - CARD) / 2})`}>{numeralCard(0, a, "card-a")}</g>
      <text x={opX + 12} y={height / 2 + 10} textAnchor="middle" fontSize="30" fill={OP_COLOR}>{"−"}</text>
      <g transform={`translate(${dotsX}, ${dotsYOffset})`}>
        {Array.from({ length: dots }).map((_, i) => {
          const cx = PAD + (i % COLS) * CELL;
          const cy = PAD + Math.floor(i / COLS) * CELL;
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r={R} fill={DOT_REMOVED_BG} stroke={DOT_REMOVED_STROKE} strokeWidth="1.5" />
              <line x1={cx - +xArm} y1={cy - +xArm} x2={cx + +xArm} y2={cy + +xArm} stroke={X_COLOR} strokeWidth="2.5" strokeLinecap="round" />
              <line x1={cx + +xArm} y1={cy - +xArm} x2={cx - +xArm} y2={cy + +xArm} stroke={X_COLOR} strokeWidth="2.5" strokeLinecap="round" />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/** MA-625 EquationRenderer — two numeral cards and the operator glyph. NO
 *  dots at all (pure recall/strategy stage), no "=" and no answer slot —
 *  the tiles themselves are the whole picture. */
export function EquationRenderer({ a, b, op }: MathRendererProps) {
  const opW = 46;
  const width = CARD * 2 + opW;
  const height = CARD;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ maxWidth: 260 }} role="img"
      aria-label={`Equation card. ${safeCardValue(a)} ${opGlyph(op)} ${safeCardValue(b)}.`}>
      {numeralCard(0, a, "card-a")}
      <text x={CARD + opW / 2} y={height / 2 + 10} textAnchor="middle" fontSize="30" fill={OP_COLOR}>{opGlyph(op)}</text>
      {numeralCard(CARD + opW, b, "card-b")}
    </svg>
  );
}
